import React, { forwardRef, useRef } from 'react';
import { Trans } from 'react-i18next';
import type { View } from 'react-native';

import { Button, Text, VStack, theme } from '@squire/ui';

import { ConsentEmailIcon } from '@/components/_icons/ConsentEmailIcon';
import { ConsentSmsIcon } from '@/components/_icons/ConsentSmsIcon';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { TermsWebViewModal } from '@/components/_shared/TermsWebViewModalProps';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { NotificationConsentProps } from './types';

const NotificationConsent = forwardRef<View, NotificationConsentProps>(
  ({ channel, testID, onAgree, onSkip, ...props }, ref) => {
    const { t } = useScreenTranslations('NotificationConsent');
    const termsWebViewModalRef = useRef<BottomSheetRef>(null);

    const ChannelIcon = channel === 'email' ? ConsentEmailIcon : ConsentSmsIcon;

    return (
      <>
        <VStack {...props} ref={ref} testID={testID} space={theme.spacing[8]}>
          <VStack
            centerHorizontal
            centerVertical
            space={theme.spacing[7]}
            paddingHorizontal={theme.spacing[2]}
          >
            <ChannelIcon size={80} />

            <Text align="center" variant="m" color="textSecondary">
              {t(`${channel}.description`)}
            </Text>
            <Text align="center" variant="m" color="textSecondary">
              <Trans
                i18nKey={`NotificationConsent.${channel}.descriptionTerms`}
                components={{
                  modalLink: (
                    <Text
                      variant="m"
                      color="textSecondary"
                      children=""
                      style={styles.link}
                      onPress={() => termsWebViewModalRef.current?.open()}
                    />
                  ),
                }}
              />
            </Text>

            <VStack style={styles.buttonContainer}>
              <Button
                variant="primary"
                onPress={onAgree}
                testID={`notification-consent-${channel}-agree`}
                accessibilityLabel={t(`accessibility.${channel}.yes`)}
              >
                {t('yes')}
              </Button>
              <Button
                variant="secondary"
                onPress={onSkip}
                testID={`notification-consent-${channel}-skip`}
                accessibilityLabel={t(`accessibility.${channel}.skip`)}
              >
                {t('skip')}
              </Button>
            </VStack>
          </VStack>
        </VStack>

        <TermsWebViewModal ref={termsWebViewModalRef} />
      </>
    );
  },
);

NotificationConsent.displayName = 'NotificationConsent';

export { NotificationConsent };
